import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Dialog, Button, TextField, MenuItem, IconButton, Box, Typography, ToggleButtonGroup, ToggleButton, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { modalHeaderSx, modalCloseIconSx, modalSaveSx, modalCloseSx } from '../../../pages/Inventory/Common/inventory.helper';
import { ranges, getRangeDates } from './invoiceDateRanges.utils';
import { getTodayYMD, formatDateDMY as formatDateDMYUtil } from '../../../utils/dateUtils';

export interface InvoiceFilterValues {
  fromDate: string;
  toDate: string;
  billType: string;
  status?: string;
}

interface InvoiceFilterProps {
  open: boolean;
  onClose: () => void;
  onApply: (values: InvoiceFilterValues) => void;
  billTypeOptions?: { value: string; label: string }[];
  statusOptions?: { value: string; label: string }[];
  // NEW: allow parent to pass initial values to prefill the modal
  initialValues?: InvoiceFilterValues;
  showBillType?: boolean;
  showStatus?: boolean;
}

const DEFAULT_VALUES: InvoiceFilterValues = {
  fromDate: '',
  toDate: '',
  billType: '',
  status: '',
};

const STORAGE_KEY = 'invoiceFilter.lastApplied';

/**
 * Format YYYY-MM-DD -> DD/MM/YYYY using dateUtils.
 * Returns empty string for falsy input (matches original behaviour).
 */
const formatDDMMYYYY = (iso?: string): string => {
  if (!iso) return '';
  return formatDateDMYUtil(iso) || '';
};

const InvoiceFilter: React.FC<InvoiceFilterProps> = ({ open, onClose, onApply, billTypeOptions = [], statusOptions = [], initialValues, showBillType=true, showStatus=false }) => {
  const { t : tBilling } = useTranslation('billing_config');
   
  // Map range keys to translation keys
  const getRangeLabel = useCallback((key: string) => {
    const rangeKeyMap: Record<string, string> = {
      'Today': 'invoice.ranges.today',
      'Last_7': 'invoice.ranges.last_7',
      'Last_30': 'invoice.ranges.last_30',
      'This_Month': 'invoice.ranges.this_month',
      'Last_Month': 'invoice.ranges.last_month',
    };
    return tBilling(rangeKeyMap[key] || key);
  }, [tBilling]);

  const [values, setValues] = useState(initialValues ?? DEFAULT_VALUES);
  const [lastApplied, setLastApplied] = useState(initialValues ?? DEFAULT_VALUES);
  const [selectedRange, setSelectedRange] = useState<string>(''); // add preset selection

  const firstFieldRef = useRef<HTMLInputElement | null>(null);
  const liveRegionRef = useRef<HTMLDivElement | null>(null);

  // On open: prefer parent initialValues; otherwise load from sessionStorage
  useEffect(() => {
    if (!open) return;

    let base = initialValues ?? DEFAULT_VALUES;

    if (!initialValues) {
      try {
        const saved = sessionStorage.getItem(STORAGE_KEY);
        if (saved) {
          const parsed = JSON.parse(saved) as InvoiceFilterValues;
          base = parsed;
        }
      } catch {
        // ignore parse errors
      }
    }

    setValues(base);
    setLastApplied(base);

    // Pre-select a range if values match one
    const matchKey =
      ranges.find((r) => {
        const d = getRangeDates(r.key);
        return d.fromDate === (base?.fromDate ?? '') && d.toDate === (base?.toDate ?? '');
      })?.key ?? '';
    setSelectedRange(matchKey);
  }, [open, initialValues]);

  // Focus the first field on open
  useEffect(() => {
    if (open && firstFieldRef.current) {
      firstFieldRef.current.focus();
    }
  }, [open]);

  const todayStr = useMemo(() => getTodayYMD(), []);

  // Basic date validation
  const dateError = useMemo(() => {
    if (!values.fromDate || !values.toDate) return '';
    // Compare YYYY-MM-DD strings lexicographically (safe for ISO date format)
    return values.toDate < values.fromDate ? tBilling('invoice.filter.date_error') : '';
  }, [values.fromDate, values.toDate, tBilling]);

  // Announce validation changes (screen readers)
  useEffect(() => {
    if (!open || !liveRegionRef.current) return;
    const message = dateError
      ? `${tBilling('invoice.filter.date_error')}.`
      : '';
    liveRegionRef.current.textContent = message;
  }, [open, dateError, tBilling]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    setSelectedRange(''); // manual changes clear preset
  }, []);

  const handleClear = useCallback(() => {
    setValues(DEFAULT_VALUES);
    setSelectedRange('');
  }, []);

  const isClearDisabled = useMemo(() => {
    return !values.fromDate && !values.toDate && !values.billType && !values.status;
  }, [values]);

  const handleApply = useCallback(() => {
    onApply(values);
    setLastApplied(values);
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(values));
    } catch {
      // ignore storage errors
    }
    onClose();
  }, [onApply, onClose, values]);

  // Disable Apply if invalid or unchanged
  const isUnchanged = useMemo(() => {
    return values.fromDate === lastApplied.fromDate &&
           values.toDate === lastApplied.toDate &&
           values.billType === lastApplied.billType &&
           values.status === lastApplied.status;
  }, [values, lastApplied]);

  const isApplyDisabled = !!dateError || isUnchanged;

  // Handle quick range selection
  const handleRangeSelect = (_: React.MouseEvent<HTMLElement>, next: string | null) => {
    if (!next) return;
    setSelectedRange(next);
    const rangeDates = getRangeDates(next);
    setValues((prev) => ({ ...prev, ...rangeDates }));
  };

  // Keyboard accessibility: Enter applies when valid, Esc closes
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
    if (e.key === 'Enter' && !isApplyDisabled) {
      e.preventDefault();
      handleApply();
    }
  };

  // Derived display strings
  const fromDateDisplay = useMemo(() => formatDDMMYYYY(values.fromDate), [values.fromDate]);
  const toDateDisplay = useMemo(() => formatDDMMYYYY(values.toDate), [values.toDate]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="invoice-filter-modal-title"
      aria-describedby="invoice-filter-modal-description"
      data-testid="invoice-filter-modal"
      PaperProps={{
        sx: {
          borderRadius: 4,
          p: 0,
          width: 480,
          mx: 'auto',
          mt: 0,
          boxShadow: 12,
          position: 'absolute',
          top: 250,
          left: 0,
          right: 0,
          overflow: 'hidden',
          outline: 'none',
        },
      }}
      sx={{ alignItems: 'flex-start', justifyContent: 'flex-start' }}
      onKeyDown={handleKeyDown}
    >
      {/* Visually-hidden live region for validation announcements */}
      <Box
        component="div"
        ref={liveRegionRef}
        sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)' }}
        aria-live="polite"
        aria-atomic="true"
      />
      <Box 
        component="header"
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        p={1} 
        px={2} 
        mb={1.5} 
        borderBottom={'1px solid rgba(226, 227, 229, 1)'}
      >
        <Typography id="invoice-filter-modal-title" sx={modalHeaderSx}>
          {tBilling('invoice.filter.title')}
        </Typography>
        <IconButton 
          data-testid="invoice-filter-modal-close" 
          onClick={onClose} 
          sx={modalCloseIconSx} 
          size="small" 
          aria-label="Close invoice filter modal"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Quick Ranges */}
      <Box px={4} pb={0}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography 
            id="quick-ranges-label" 
            variant="body2" 
            sx={{ mb: 0, color: 'text.secondary', fontWeight: 600 }}
          >
            {tBilling('invoice.filter.quick_ranges')}
          </Typography>
          {selectedRange && (
            <Box 
              component="span" 
              id="applied-range-display"
              sx={{ fontSize: '12px', color: 'text.secondary' }}
              role="status"
              aria-live="polite"
            >
              {fromDateDisplay && toDateDisplay
                ? `${tBilling('invoice.filter.applied')}: ${fromDateDisplay} – ${toDateDisplay}`
                : `${tBilling('invoice.filter.applied')}: ${getRangeLabel(selectedRange)}`}
            </Box>
          )}
        </Box>
        <ToggleButtonGroup
          value={selectedRange}
          exclusive
          onChange={handleRangeSelect}
          aria-labelledby="quick-ranges-label"
          aria-label="Quick date range presets"
          role="group"
          sx={{
            flexWrap: 'wrap',
            gap: 1,
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              border: '1px solid #e0e0e0',
              borderRadius: '16px',
              px: 1.25,
              py: 0.5,
            },
          }}
        >
          {ranges.map((r) => (
            <ToggleButton 
              key={r.key} 
              value={r.key} 
              data-testid={`invoice-filter-range-${r.key.toLowerCase()}`} 
              aria-label={getRangeLabel(r.key)}
              aria-pressed={selectedRange === r.key}
            >
              {getRangeLabel(r.key)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      <Box component="fieldset" px={4} pb={0} sx={{ border: 'none', margin: 0 }}>
        <legend style={{ display: 'none' }}>Date and filter options</legend>
        <TextField
          data-testid="invoice-filter-date-from"
          label={tBilling('invoice.filter.from_date')}
          name="fromDate"
          type="date"
          value={values.fromDate}
          onChange={handleChange}
          fullWidth
          placeholder="DD/MM/YYYY"
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: { 
              ref: firstFieldRef,
              style: { textTransform: 'uppercase' }, 
              max: todayStr,
              'aria-describedby': 'from-date-help'
            },
          }}
          sx={{ mb: 2 }}
        />
        <TextField
          data-testid="invoice-filter-date-to"
          label={tBilling('invoice.filter.to_date')}
          name="toDate"
          type="date"
          value={values.toDate}
          onChange={handleChange}
          fullWidth
          placeholder="DD/MM/YYYY"
          error={!!dateError}
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: {
              style: { textTransform: 'uppercase' },
              min: values.fromDate || undefined,
              max: todayStr,
              'aria-describedby': dateError ? 'date-error' : undefined,
            },
            formHelperText: { id: 'date-error' },
          }}
          sx={{ mb: 2 }}
          helperText={dateError || undefined}
        />
        {showBillType && (
          <TextField
            data-testid="invoice-filter-bill-type"
            select
            label={tBilling('invoice.filter.bill_type')}
            name="billType"
            value={values.billType}
            onChange={handleChange}
            fullWidth
            slotProps={{
              select: { displayEmpty: true },
              inputLabel: { shrink: true },
              htmlInput: { 
                style: { textTransform: 'capitalize' },
                'aria-label': 'Bill type filter'
              },
            }}
            sx={{ mb: 2 }}
          >
            {billTypeOptions.map((opt) => (
              <MenuItem 
                key={opt.value} 
                value={opt.value} 
                data-testid={`invoice-filter-bill-type-option-${opt.value.toLowerCase()}`} 
                disabled={opt.value === ''}
              >
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        )}
        {showStatus && statusOptions && statusOptions.length > 0 && (
          <TextField
            data-testid="invoice-filter-status"
            select
            label={tBilling('invoice.filter.status')}
            name="status"
            value={values.status}
            onChange={handleChange}
            fullWidth
            slotProps={{
              select: { 
                displayEmpty: true,
                renderValue: (value: unknown) => {
                  if (!value) {
                    return <span style={{ color: '#9e9e9e' }}>Select Status</span>;
                  }
                  const selected = statusOptions.find(opt => opt.value === value);
                  return selected?.label || String(value);
                }
              },
              inputLabel: { shrink: true },
              htmlInput: { 
                style: { textTransform: 'capitalize' },
                'aria-label': 'Status filter'
              },
            }}
            sx={{ mb: 2 }}
          >
            {statusOptions
              .filter(opt => opt.value !== '')
              .map((opt) => (
                <MenuItem 
                  key={opt.value} 
                  value={opt.value}
                  data-testid={`invoice-filter-status-option-${opt.value.toLowerCase()}`}
                >
                  {opt.label}
                </MenuItem>
              ))}
          </TextField>
        )}
      </Box>

      <Box component="section" display="flex" justifyContent="space-between" gap={2} p={3} pt={0}>
        <Button 
          data-testid="invoice-filter-clear" 
          variant="outlined" 
          onClick={handleClear} 
          sx={modalCloseSx} 
          disabled={isClearDisabled} 
          aria-label="Clear all invoice filters"
        >
          {tBilling('invoice.filter.clear')}
        </Button>
        <Button 
          data-testid="invoice-filter-apply" 
          variant="contained" 
          onClick={handleApply} 
          sx={modalSaveSx} 
          disabled={isApplyDisabled} 
          aria-label="Apply invoice filters"
        >
          {tBilling('invoice.filter.apply')}
        </Button>
      </Box>
    </Dialog>
  );
};

export default InvoiceFilter;
